import { authFetch } from '../request';

// ─── Types（与后端 playable-doctor.service.ts 保持一致） ──────────────

export type DoctorSeverity = 'P0' | 'P1' | 'P2';
export type DoctorCategory =
  | 'structure'
  | 'sdk'
  | 'syntax'
  | 'event'
  | 'lifecycle'
  | 'tracking'
  | 'media'
  | 'logic'
  | 'log'
  | 'delivery';

export interface DoctorIssue {
  id: string;
  severity: DoctorSeverity;
  category: DoctorCategory;
  title: string;
  detail: string;
  file?: string;
  line?: number;
  snippet?: string;
  fixable: boolean;
  fixSummary?: string;
  fixed?: boolean;
}

export interface DoctorFileInfo {
  path: string;
  size: number;
  isEntry: boolean;
  isSdk: boolean;
  isCover: boolean;
}

export interface DoctorChange {
  issueId: string;
  file: string;
  description: string;
  before: string;
  after: string;
}

export interface DoctorReport {
  fileName: string;
  entryFile: string | null;
  coverImage: string | null;
  score: number;
  /** 修复前评分（仅 fix 返回） */
  beforeScore?: number;
  /** 修复前问题概览（仅 fix 返回） */
  beforeSummary?: { total: number; p0: number; p1: number; p2: number };
  summary: {
    total: number;
    p0: number;
    p1: number;
    p2: number;
    fixable: number;
    fixedCount: number;
  };
  files: DoctorFileInfo[];
  issues: DoctorIssue[];
  changes: DoctorChange[];
}

export interface DoctorFixResult {
  report: DoctorReport;
  zipFileName: string;
  zipBase64: string;
  historyId?: string;
}

/** 修复分组（与后端 DOCTOR_FIX_GROUPS 一致） */
export interface DoctorFixGroup {
  key: string;
  label: string;
  hint: string;
  ruleIds: string[];
}

/** 历史修复记录 */
export interface DoctorHistoryEntry {
  id: string;
  createdAt: string;
  sourceName: string;
  zipName: string;
  beforeScore: number;
  score: number;
  summary: { total: number; p0: number; p1: number; p2: number; fixable: number; fixedCount: number };
  changeIds: string[];
  disabled: string[];
  sizeBytes: number;
}

// ─── API ──────────────────────────────────────────────

async function parseOrThrow<T>(res: Response, action: string): Promise<T> {
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.message || body?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(`${action}失败：${res.status}${detail ? `（${detail}）` : ''}`);
  }
  const json = await res.json();
  if (!json?.success) {
    throw new Error(json?.error || `${action}失败`);
  }
  return json.data as T;
}

/** 上传试玩 zip / html，返回诊断报告（不改动原文件） */
export async function analyzePlayablePackage(file: File): Promise<DoctorReport> {
  const form = new FormData();
  form.append('file', file);
  const res = await authFetch('/api/playable-doctor/analyze', { method: 'POST', body: form });
  return parseOrThrow<DoctorReport>(res, '诊断');
}

/** 上传试玩 zip / html，自动修复并返回修复后的 zip（base64）与修复后报告；disabled 为取消勾选的分组 key */
export async function fixPlayablePackage(file: File, disabled: string[] = []): Promise<DoctorFixResult> {
  const form = new FormData();
  form.append('file', file);
  if (disabled.length) form.append('disabled', JSON.stringify(disabled));
  const res = await authFetch('/api/playable-doctor/fix', { method: 'POST', body: form });
  return parseOrThrow<DoctorFixResult>(res, '修复');
}

/** 拉取修复分组清单（页面复选框） */
export async function fetchFixGroups(): Promise<DoctorFixGroup[]> {
  const res = await authFetch('/api/playable-doctor/fix-groups');
  return parseOrThrow<DoctorFixGroup[]>(res, '加载修复分组');
}

/** 拉取历史修复记录 */
export async function fetchHistory(): Promise<DoctorHistoryEntry[]> {
  const res = await authFetch('/api/playable-doctor/history');
  return parseOrThrow<DoctorHistoryEntry[]>(res, '加载历史记录');
}

/** 删除一条历史记录 */
export async function deleteHistoryEntry(id: string): Promise<void> {
  const res = await authFetch(`/api/playable-doctor/history/${id}`, { method: 'DELETE' });
  await parseOrThrow<unknown>(res, '删除历史记录');
}

/** 下载历史修复包（GET 二进制） */
export async function downloadHistoryZip(id: string, fileName: string): Promise<void> {
  const res = await authFetch(`/api/playable-doctor/history/${id}/download`);
  if (!res.ok) throw new Error(`下载失败：${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** base64 zip → 触发浏览器下载 */
export function downloadBase64Zip(base64: string, fileName: string): void {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) {
    bytes[i] = bin.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** 下载 JSON 报告（便于留存/对比） */
export function downloadReportJson(report: DoctorReport, fileName?: string): void {
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || `${report.fileName.replace(/\.(zip|html?)$/i, '')}-report.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}